<template>
  <div class="checkout-wrap wrapper relative">
    <button class="btn btn-back hidden-xs top-65" @click.prevent="handleBack('/')">
      <span>{{ trans.text.checkout.back }}</span>
    </button>
    <div class="checkout">

      <div class="checkout-heading">
        <h2>{{ trans.text.checkout.checkout }}</h2>
        <p>{{ trans.text.checkout['paymentWarn'] }}</p>
      </div>

      <div class="checkout-form">

        <div v-if="showRegisterForm" class="checkout-form-row">
          <h4>{{ trans.text.checkout['accountData'] }}:</h4>
          <div ref="email" class="inp-group">
            <label for="chk-email">E-Mail</label>
            <input
              id="chk-email"
              v-model="email"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div ref="f_name" class="inp-group">
            <label for="chk-name">{{ trans.text.checkout['nameLabel'] }}</label>
            <input
              id="chk-name"
              v-model="f_name"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div ref="l_name" class="inp-group">
            <label for="chk-surname">{{ trans.text.checkout['surnameLabel'] }}</label>
            <input
              id="chk-surname"
              v-model="l_name"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div ref="password" class="inp-group">
            <label for="chk-password">{{ trans.text.checkout['passwordLabel'] }}</label>
            <input
              id="chk-password"
              v-model="password"
              class="inp-style inp-fw"
              required
              type="password"
            >
          </div>

          <div ref="password_confirmation" class="inp-group">
            <label for="chk-password-confirmation">{{ trans.text.checkout['retypePasswordLabel'] }}</label>
            <input
              id="chk-password-confirmation"
              v-model="password_confirmation"
              class="inp-style inp-fw"
              required
              type="password"
            >
          </div>
        </div>

        <div class="checkout-form-row">
          <h4>{{ trans.text.checkout['deliveryAddress'] }}:</h4>
          <div class="inp-group">
            <label>{{ trans.text.checkout['deliveryTypeLabelText'] }}</label>
            <div
              v-click-outside="hideDeliveryTypePopup"
              :class="deliveryTypeSelectorClass"
              class="select-delivery-type"
              @click="toggleDeliveryTypePopup"
            >
              <div :class="{pickup: deliveryType === 'pickup'}" class="selected-delivery-type">
                {{ deliveryTypeHuman }}
              </div>
              <div class="delivery-types-wrapper">
                <div
                  :class="{selected: deliveryType === 'new'}"
                  class="item"
                  @click="selectDeliveryType('new')"
                >
                  <div class="title new-address">{{ trans.text.checkout['newAddressSelectLabel'] }}</div>
                </div>

                <div
                  :class="{selected: deliveryType === 'pickup'}"
                  class="item"
                  @click="selectDeliveryType('pickup')"
                >
                  <div class="title">{{trans.text.checkout['FTCTitleInSelector']}}</div>
                  <div class="info">{{ trans.text.checkout['FTCInfoInSelector'] }}</div>
                </div>

                <div
                  v-for="(address, index) in deliveryAddresses"
                  :key="address.id"
                  :class="{selected: deliveryType === address.id}"
                  class="item"
                  @click="selectDeliveryType(address.id)"
                >
                  <div class="title">{{ trans.text.checkout['mySavedAddressPrefix'] }} #{{ index + 1 }}</div>
                  <div class="info">
                    <div v-if="address.streetNo">{{ address.streetNo }}</div>
                    <div v-if="address.flat">{{ address.flat }}</div>
                    <div v-if="address.zip">{{ address.zip }}</div>
                    <div v-if="address.town_name">{{ address.town_name }}</div>
                    <div v-if="address.state">{{ address.state }}</div>
                    <div v-if="address['country_name']">{{ address['country_name'] }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="inp-group">
            <label ref="recipient" for="chk-rec">{{ trans.text.checkout['recipientName'] }}</label>
            <input
              id="chk-rec"
              v-model="recipient"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div ref="phone" class="inp-group">
            <label for="chk-phone">Phone</label>
            <input
              id="chk-phone"
              v-model="phone"
              v-mask="'+###############'"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-row inp-row--street">
            <div class="inp-group">
              <label ref="streetNo" for="chk-street">{{ trans.text.checkout['streetNameAndNumber'] }}</label>
              <input
                id="chk-street"
                v-model="computedStreetNo"
                :disabled="isPickupOrPreSelected"
                class="inp-style inp-fw"
                required
                type="text"
              >
            </div>

            <div class="inp-group">
              <label ref="flat" for="chk-flat">{{ trans.text.checkout.flat }}</label>
              <input
                id="chk-flat"
                v-model="computedFlat"
                :disabled="isPickupOrPreSelected"
                class="inp-style inp-fw"
                required
                type="text"
              >
            </div>
          </div>

          <div class="inp-group">
            <label ref="zip" for="chk-zip">{{ trans.text.checkout.zip }}</label>
            <input
              id="chk-zip"
              v-model="computedZip"
              :disabled="isPickupOrPreSelected"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="town_name" for="chk-town">{{ trans.text.checkout.city }}</label>
            <input
              id="chk-town"
              v-model="computedTownName"
              :disabled="isPickupOrPreSelected"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="state" for="chk-state">{{ trans.text.checkout.state }}</label>
            <input
              id="chk-state"
              v-model="computedState"
              :disabled="isPickupOrPreSelected"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="country_id">{{ trans.text.checkout.country }}</label>
            <multiselect
              v-model="computedCountryId"
              :allow-empty="false"
              :disabled="isPickupOrPreSelected"
              :label="'title'"
              :limit="5"
              :options="countries"
              :placeholder="trans.text.checkout['selectCountry']"
              :preselect-first="true"
              :searchable="true"
              :showNoResults="false"
              :track-by="'id'"
              deselectLabel=""
              selectLabel=""
              selectedLabel="">
            </multiselect>
          </div>
        </div>

        <div v-if="!isPickup" class="checkout-form-row">
          <h4 class="mb-2">{{ trans.text.checkout['invoiceAddress'] }}:</h4>

          <div class="inp-group">
            <multiselect
              v-model="same_delivery_option"
              :allow-empty="false"
              :internal-search="false"
              :label="'title'"
              :limit="5"
              :options="deliveryAddressOptions"
              :placeholder="trans.text.checkout['selectInvoiceAddress']"
              :preselect-first="true"
              :searchable="false"
              :showNoResults="false"
              :track-by="'id'"
              class="changeBackground"
              deselectLabel=""
              selectLabel=""
              selectedLabel="">
            </multiselect>
          </div>
        </div>

        <div v-if="!same_delivery && !isPickup" class="checkout-form-row">
          <h4>{{ trans.text.checkout['yourInvoiceAddress'] }}:</h4>
          <div class="inp-group">
            <label ref="inv_recipient" for="chk-rec_inv">{{ trans.text.checkout['recipientName'] }}</label>
            <input
              id="chk-rec_inv"
              v-model="inv_recipient"
              class="inp-style inp-fw" required
              type="text"
            >
          </div>

          <div class="inp-row inp-row--street">
            <div class="inp-group">
              <label ref="inv_streetNo" for="chk-street_inv">
                {{ trans.text.checkout['streetNameAndNumber'] }}
              </label>
              <input
                id="chk-street_inv"
                v-model="inv_streetNo"
                class="inp-style inp-fw"
                required
                type="text"
              >
            </div>

            <div class="inp-group">
              <label ref="inv_flat" for="chk-flat_inv">{{ trans.text.checkout.flat }}</label>
              <input
                id="chk-flat_inv"
                v-model="inv_flat"
                class="inp-style inp-fw"
                type="text"
              >
            </div>
          </div>

          <div class="inp-group">
            <label ref="inv_zip" for="chk-zip_inv">{{ trans.text.checkout.zip }}</label>
            <input
              id="chk-zip_inv"
              v-model="inv_zip"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="inv_town_name" for="chk-town_inv">{{ trans.text.checkout.city }}</label>
            <input
              id="chk-town_inv"
              v-model="inv_town_name"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="inv_state" for="chk-state_inv">{{ trans.text.checkout.state }}</label>
            <input
              id="chk-state_inv"
              v-model="inv_state"
              class="inp-style inp-fw"
              required
              type="text"
            >
          </div>

          <div class="inp-group">
            <label ref="inv_country_id">{{ trans.text.checkout.country }}</label>
            <multiselect
              v-model="inv_country_id"
              :allow-empty="false"
              :label="'title'"
              :limit="5"
              :options="countries"
              :placeholder="trans.text.checkout['selectCountry']"
              :preselect-first="true"
              :searchable="true"
              :showNoResults="false"
              :track-by="'id'"
              deselectLabel=""
              selectLabel=""
              selectedLabel="">
            </multiselect>
          </div>
        </div>

        <div class="checkout-info" ref="checkboxesErrorScrollRef">
          <ul>
            <li>{{ trans.text.checkout['productionStart'] }}: <b>{{ trans.text.checkout['productionDate'] }}</b></li>
            <li>
              {{ trans.text.checkout['forecastDeliveryDate'] }}:
              <b>{{ deliveryDate }}</b>
              <br>
            </li>
          </ul>
        </div>

        <p class="checkout-text">{{ trans.text.checkout['worldWideShipping'] }}</p>
        <div class="checkout-price">
          <div class="item">
            <p><strong>{{ trans.text.checkout.subTotal }}</strong></p>
            <p>{{ subTotalMoney }}</p>
          </div>
          <div class="item">
            <p>{{ trans.text.checkout.shipping }}</p>
            <p>{{ trans.text.checkout.free }}</p>
          </div>
          <div class="item result">
            <p>{{ trans.text.checkout.total }}</p>
            <p>{{ subTotalMoney }}
              <span v-if="!country_id">{{ trans.text.checkout.tax }}</span>
              <span v-else>{{ taxTitleFormated }}</span>
            </p>
          </div>
        </div>

        <div class="inp-groups inp-groups__checkout">
          <div class="inp-group">
            <input
              id="styled-checkbox-1"
              v-model="dataProtectionClause"
              class="inp-group__checkbox"
              type="checkbox"
              ref="dataProtectionClause"
            >
            <label for="styled-checkbox-1">
              <span class="checkbox"></span>
              <span>
              {{ trans.text.checkout['iHaveRead'] }}
              <a :href="`/${lang}/page/data-protection-basic-information-purchase`" target="_blank">
                {{ trans.text.checkout.data }}
              </a>
              {{ trans.text.checkout.clause }}
            </span>
            </label>
          </div>

          <div class="inp-group">
            <input
              id="styled-checkbox-3"
              v-model="terms"
              class="inp-group__checkbox"
              type="checkbox"
              ref="terms"
            >
            <label for=styled-checkbox-3>
              <span class="checkbox"></span>
              <span>
              {{ trans.text.checkout['iHaveRead'] }}
              <a :href="`/${lang}/page/general-terms-and-conditions-of-sale`" target="_blank">
                {{ trans.text.checkout.terms }}
              </a>.
            </span>
            </label>
          </div>

          <div class="inp-group">
            <input
              id="styled-checkbox-2"
              v-model="authorizing"
              class="inp-group__checkbox"
              type="checkbox"
              ref="authorizing"
            >
            <label for="styled-checkbox-2">
              <span class="checkbox"></span>
              <span>{{ trans.text.checkout['iAuthorize'] }}</span>
            </label>
          </div>
        </div>
      </div>

      <p v-if="getReservationEnded || showChooseAnotherOptionsButtons" class="checkout-text-bold">
        {{ trans.text.checkout['reservationEndedInfo'] }}
      </p>

      <div v-if="false" class="checkout-controls with-margin">
        <button
          v-if="!getReservationEnded && !showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block btn-card_pay"
          @click.prevent="order('ecompay')">
        <span v-if="isLoading && paymentType === 'ecompay'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'ecompay'">
            {{ trans.text.checkout['completeCheckout'] }}
        </span>
        </button>
      </div>

      <div v-if="false" class="checkout-controls with-margin">
        <button
          v-if="!getReservationEnded && !showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block black"
          @click.prevent="order('apple')">
        <span v-if="isLoading && paymentType === 'apple'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'apple'">
          {{ trans.text.checkout['payByApple'] }}
          <span class="apple-ic"></span>
        </span>
        </button>
      </div>

      <div v-if="true" class="checkout-controls with-margin">
        <button
          v-if="!getReservationEnded && !showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block btn-paypal_pay blue"
          @click.prevent="order('paypal')">
        <span v-if="isLoading && paymentType === 'paypal'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'paypal'">
            {{ trans.text.checkout['payByPayPal'] }} <span class="paypal-ic"></span>
        </span>
        </button>
      </div>

      <div v-if="true" class="checkout-controls with-margin">
        <p>If you want to use another payment method, or have questions about how to complete your purchase, please save
          your order details by clicking on "Save my order", and contact us at <a href="mailto:contact@forestal.com"
                                                                                  target="_blank">contact@forestal.com</a>
        </p>
      </div>
      <div v-if="true" class="checkout-controls with-margin">
        <button
          v-if="!getReservationEnded && !showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block blue"
          @click.prevent="order('save')">
        <span v-if="isLoading && paymentType === 'save'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'save'">
          Save my order
        </span>
        </button>
      </div>

      <div
        v-if="getReservationEnded || showChooseAnotherOptionsButtons"
        class="checkout-controls with-margin"
        @click.prevent="chooseAnother"
      >
        <button class="btn btn-block btn-green">
          {{ trans.text.checkout['chooseAgain'] }}
        </button>
      </div>
      <div v-if="getReservationEnded || showChooseAnotherOptionsButtons" class="checkout-controls with-margin">
        <button
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block btn-card_pay"
          @click.prevent="order('ecompay')"
        >
          {{ trans.text.checkout['withoutSelecting'] }}
        </button>
      </div>

      <div v-if="false" class="checkout-controls with-margin">
        <button
          v-if="getReservationEnded || showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block btn-apple_pay"
          @click.prevent="order('apple')"
        >
        <span v-if="isLoading && paymentType === 'apple'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'apple'">
            {{ trans.text.checkout['payByApple'] }}
        </span>
        </button>
      </div>

      <div v-if="true" class="checkout-controls">
        <button
          v-if="getReservationEnded || showChooseAnotherOptionsButtons"
          :class="{'is-disabled': isDisabled, 'btn-gray': isDisabled, 'btn-green': !isDisabled}"
          :disabled="isDisabled"
          class="btn btn-block btn-paypal_pay"
          @click.prevent="order('paypal')"
        >
        <span v-if="isLoading && paymentType === 'paypal'">
            {{ trans.text.checkout['waitForCheckoutComplete'] }}
        </span>
          <span v-if="paymentType !== 'paypal'">
            {{ trans.text.checkout['payByPayPal'] }}
        </span>
        </button>
      </div>

      <v-modal :body="modalBody" :options="modalOptions" :type="modalType" name=""/>

      <div class="modalPrimary modal-sm custom-primary-modal" :class="{active: deliveryNotAvailable}">
        <button class="close max-md-hidden" @click.prevent="hideDeliveryModal">x</button>
        <div class="modalBox text-center">
          <button class="close md-hidden" @click.prevent="hideDeliveryModal">x</button>
          <p class="text-lg">
            <span class="medium">Dear customer,</span>
            <span>Unfortunately, direct shipments are currently available via checkout only countries inside the European continent.</span>
            <span>For shipments to countries outside Europe, please contact us at
              <a href="mailto:contact@forestal.com">contact@forestal.com</a>
              аnd we will provide you with all the information necessary to complete your order and deliver it to your address in the most efficient way.
            </span>
          </p>
          <p class="text-lg">
            <a href="#" @click.prevent="generateInvoice">Download order</a>
          </p>

          <div class="btn-modal-group">
            <button type="button" class="btn btn-green btn-sm w-100" @click.prevent="order('save', true)">
              Save the order
            </button>
            <button @click.prevent="navigateToDealers" type="button" class="btn btn-gray btn-sm w-100">
              Contact the dealer
            </button>
          </div>
        </div>
      </div>

      <div class="modalPrimary modal-sm custom-primary-modal" :class="{active: errorModal}">
        <div class="modalBox text-center">
          <p class="text-lg">
            <span>{{ errorText }}</span>
          </p>

          <div class="btn-modal-group">
            <button type="button" class="btn btn-green btn-sm w-100" @click.prevent="errorModal = false">
              Ok
            </button>
          </div>
        </div>
      </div>

      <div class="modalPrimary modal-sm custom-primary-modal" :class="{active: orderSavedModalVisibility}">
        <div class="modalBox text-center">
          <p class="text-lg">
            <span>Thank you for saving your order. It will be saved in your shopping cart for up to 10 days so that you can complete your purchase within that period of time.</span>
          </p>

          <div class="btn-modal-group">
            <button type="button" class="btn btn-green btn-sm w-100" @click.prevent="navigateToProfile">
              Ok, I understand
            </button>
          </div>
        </div>
      </div>

      <loader-component v-show="showLoader"></loader-component>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import Multiselect from "vue-multiselect";
import ClickOutside from "vue-click-outside";
import LoaderComponent from "./LoaderComponent.vue";

import Modal from "./modals/Modal";

require("vue-multiselect/dist/vue-multiselect.min.css");
export default {
  props: ["lang", "trans", "redirect", "free"],
  name: "CheckoutComponent",
  components: {Multiselect, Modal, LoaderComponent},
  directives: {
    ClickOutside,
  },
  data() {
    return {
      errorModal: false,
      errorText: "",
      checkboxErrors: false,
      deliveryNotAvailable: false,
      orderSavedModalVisibility: false,
      showLoader: true,
      popupItem: null,
      isLoading: false,
      paymentType: null,
      modalBody: "",
      modalType: "",
      modalOptions: null,
      email: null,
      phone: null,
      f_name: null,
      l_name: null,
      password: null,
      password_confirmation: null,
      recipient: null,
      flat: null,
      streetNo: null,
      zip: null,
      state: null,
      country_id: null,
      country: null,
      town_name: null,
      same_delivery: true,
      same_delivery_option: {
        id: 1,
        title: "Same as delivery address",
      },
      inv_recipient: null,
      inv_flat: null,
      inv_streetNo: null,
      inv_zip: null,
      inv_state: null,
      inv_country_id: null,
      inv_town_name: null,
      deliveryAddressOptions: [
        {
          id: 1,
          title: this.trans.text.checkout["sameAsDeliveryAddress"],
        },
        {
          id: 2,
          title: this.trans.text.checkout["otherDeliveryAddress"],
        },
      ],
      dataProtectionClause: false,
      terms: false,
      authorizing: false,
      deliveryTypeOpen: false,
      deliveryType: "new",
      months: {
        1: "January",
        2: "February",
        3: "March",
        4: "April",
        5: "May",
        6: "June",
        7: "July",
        8: "August",
        9: "September",
        10: "October",
        11: "November",
        12: "December",
      },
      allowedCountries: [
        "Austria", "Belgium", "United Kingdom", "Germany", "Ireland", "Liechtenstein", "Luxembourg",
        "Monaco", "Netherlands", "France", "Switzerland", "Byelorussia", "Bulgaria", "Hungary",
        "Moldavia", "Poland", "Russia", "Romania", "Slovakia", "Chechia", "Ukraine",
        "Denmark", "Iceland", "Latvia", "Lithuania", "Norway", "Finland", "Estonia",
        "Sweden", "Albania", "Andorra", "Bosnia and Herzegovina", "Vatican", "Greece",
        "Spain", "Italy", "North Macedonia", "Malta", "Portugal", "San marino", "Serbia",
        "Slovenia", "Croatia", "Montenegro", "Czechia",
      ],
    };
  },
  mounted() {
    this.$root.$on("modalShow", (data) => {
      this.modalBody = data.text;
      this.modalType = data.type;
      this.modalOptions = data.options;

      setTimeout(() => {
        $("#" + this.modalType).show();
      }, 2);
    });
    this.$store.dispatch("user", {
      path: this.$path,
    }).then(() => {
      if (localStorage.getItem("basket_id")) {
        this.popupItem = this.$el;
        this.loadCartItems();
        this.loadDeliveryAddresses({
          path: this.$path,
        });
        this.same_delivery_option = {
          id: 1,
          title: this.trans.text.checkout["sameDelivery"],
        };
        this.loadCountries();
        if (!!this.getAuthUser) {
          this.recipient = this.getAuthUser.f_name + " " + (!!this.getAuthUser.l_name ? this.getAuthUser.l_name : "");
        }
      } else {
        location.href = "/en/basket";
      }
    }).catch(() => {
      location.href = "/en/login";
    });
  },
  computed: {
    ...mapGetters({
      getCountries: "getCountries",
      getAuthUser: "getAuthUser",
      getReservationEnded: "getReservationEnded",
      cartItems: "cartItems",
      deliveryAddresses: "deliveryAddresses",
      items: "baskets/items",
      subTotal: "baskets/subTotal",
      euSubtotal: "baskets/euSubtotal",
      taxTitle: "baskets/taxTitle",
      taxPercent: "baskets/taxPercent",
    }),
    subTotalMoney() {
      return this.$crConverter((!this.country_id ? this.euSubtotal : this.subTotal) / 100, "EUR");
    },
    lastItem() {
      const months = {
        "January": 1,
        "February": 2,
        "March": 3,
        "April": 4,
        "May": 5,
        "June": 6,
        "July": 7,
        "August": 8,
        "September": 9,
        "October": 10,
        "November": 11,
        "December": 12,
      };
      let maxItem = null;
      for (let item of this.items) {
        if (item.build && item.build.model && item.build.model["delivery_info"] || (item.build && item.build["delivery_date_parsed"])) {
          let month;
          let year;
          if (item.build["delivery_date_parsed"]) {
            month = item.build["delivery_date_parsed"]["month"];
            year = item.build["delivery_date_parsed"]["year"];
          } else {
            let deliveryInfo = item.build.model["delivery_info"].split(" ");
            month = deliveryInfo[0];
            year = deliveryInfo[1];
          }
          if (maxItem === null) {
            maxItem = {
              month: month,
              year: year,
              model: item.build.model,
              delivery_date: item.build["delivery_date_parsed"] || null,
            };
          } else if (year > maxItem.year) {
            maxItem = {
              month: month,
              year: year,
              model: item.build.model,
              delivery_date: item.build["delivery_date_parsed"] || null,
            };
          } else if (year === maxItem.year) {
            let maxItemMonthKey = months[maxItem.month];
            let currentMonthKey = months[month];
            if (currentMonthKey > maxItemMonthKey) {
              maxItem = {
                month: month,
                year: year,
                model: item.build.model,
                delivery_date: item.build["delivery_date_parsed"] || null,
              };
            }
          }
        }
      }
      if (maxItem !== null) {
        return maxItem;
      }
      return null;
    },
    deliveryDate() {
      const defaultDate = this.$moment().add(3, "month").format("MMMM YYYY");
      if (this.lastItem) {
        if (this.lastItem["delivery_date"]) {
          return this.months[this.lastItem["delivery_date"]["month"]] + " " + this.lastItem["delivery_date"]["year"];
        }
        return this.lastItem.model["delivery_info"] || defaultDate;
      }

      return defaultDate;
    },
    taxTitleFormated() {
      return `${this.taxTitle} ${this.taxPercent}%`;
    },
    isPickup() {
      return this.deliveryType === "pickup";
    },
    isPickupOrPreSelected() {
      return this.deliveryType !== "new";
    },
    deliveryTypeSelectorClass() {
      return {
        open: this.deliveryTypeOpen,
      };
    },
    isDisabled() {
      return !(!!this.authorizing) || !(!!this.dataProtectionClause) || !(!!this.terms) || this.isLoading;
    },
    countries() {
      return this.getCountries.map(country => {
        return {
          id: country.id,
          title: country["country_name"],
          tax: country.tax,
          slug: country.slug,
        };
      });
    },
    showRegisterForm() {
      return !(!!localStorage.getItem("access_token"));
    },
    showChooseAnotherOptionsButtons() {
      return !!this.cartItems && this.cartItems.filter(item => {
        return item["frame_number_deleted"];
      }).length > 0;
    },
    selectedDeliveryAddress() {
      return this.deliveryAddresses.find(item => {
        return item.id === this.deliveryType;
      });
    },
    computedStreetNo: {
      get() {
        if (this.deliveryType === "new") {
          return this.streetNo;
        } else if (this.deliveryType === "pickup") {
          return "CG-4, Nr. 1";
        } else {
          return this.selectedDeliveryAddress.streetNo;
        }
      },
      set(value) {
        this.streetNo = value;
      },
    },
    computedFlat: {
      get() {
        if (this.deliveryType === "new") {
          return this.flat;
        } else if (this.deliveryType === "pickup") {
          return null;
        } else {
          return this.selectedDeliveryAddress.flat;
        }
      },
      set(value) {
        this.flat = value;
      },
    },
    computedZip: {
      get() {
        if (this.deliveryType === "new") {
          return this.zip;
        } else if (this.deliveryType === "pickup") {
          return "AD400";
        } else {
          return this.selectedDeliveryAddress.zip;
        }
      },
      set(value) {
        this.zip = value;
      },
    },
    computedTownName: {
      get() {
        if (this.deliveryType === "new") {
          return this.town_name;
        } else if (this.deliveryType === "pickup") {
          return "La Massana";
        } else {
          return this.selectedDeliveryAddress.town_name;
        }
      },
      set(value) {
        this.town_name = value;
      },
    },
    computedState: {
      get() {
        if (this.deliveryType === "new") {
          return this.state;
        } else if (this.deliveryType === "pickup") {
          return "";
        } else {
          return this.selectedDeliveryAddress.state;
        }
      },
      set(value) {
        this.state = value;
      },
    },
    computedCountryId: {
      get() {
        if (this.deliveryType === "new") {
          return this.country_id;
        } else if (this.deliveryType === "pickup") {
          return this.countries.find(item => {
            return item["title"] === "Andorra";
          });
        } else {
          return this.countries.find(item => {
            return item["id"] === this.selectedDeliveryAddress.country_id;
          });
        }
      },
      set(value) {
        this.country_id = value;
      },
    },
    deliveryTypeHuman() {
      switch (this.deliveryType) {
        case "new":
          return this.trans.text.checkout["newAddressSelectLabel"];
        case "pickup":
          return this.trans.text.checkout["FTCTitleInSelector"];
        default:
          if (this.deliveryType) {
            return this.makeHumanReadableAddress();
          }
          break;
      }
    },
  },
  methods: {
    ...mapActions(['LoadCountries', 'Checkout', 'GetCartItems', 'ChooseAnotherItems', 'loadDeliveryAddresses']),
    generateInvoice() {
      this.$store.dispatch('baskets/generateInvoice', {
        path: this.$path
      }).then(response => {
        if (response?.data?.url) {
          let a = document.createElement('a')
          a.href = response.data.url
          a.target = "_blank"
          let splited = response.data.url.split("/");
          a.download = splited[splited.length - 1]

          a.click()

          setTimeout(() => {
            a.remove()
          }, 50)
        }
      })
    },
    navigateToProfile() {
      location.href = `/${this.lang}/profile#orders`
    },
    navigateToDealers() {
      const country = this.countries.find(item => item.id === this.country_id?.id)

      if (country) {
        const a = document.createElement('a')
        a.href = `/${this.lang}/dealers/${country.slug}`
        a.target = "_blank"

        a.click()

        setTimeout(() => {
          a.remove()
        }, 20)
      }
    },
    hideDeliveryModal() {
      this.deliveryNotAvailable = false
    },
    makeHumanReadableAddress() {
      let address = this.deliveryAddresses.find(item => {
        return item.id === this.deliveryType;
      });

      if (address) {
        let stringAddress = address.streetNo;
        if (address.flat) {
          stringAddress += `, ${address.flat}`;
        }
        if (address.zip) {
          stringAddress += `, ${address.zip}`;
        }
        if (address.town_name) {
          stringAddress += `, ${address.town_name}`;
        }
        if (address.state) {
          stringAddress += `, ${address.state}`;
        }
        if (address["country_name"]) {
          stringAddress += `, ${address["country_name"]}`;
        }

        return stringAddress;
      } else {
        this.deliveryType = "new";
        return "";
      }
    },
    toggleDeliveryTypePopup() {
      this.deliveryTypeOpen = !this.deliveryTypeOpen;
    },
    hideDeliveryTypePopup() {
      if (this.deliveryTypeOpen) {
        this.deliveryTypeOpen = false;
      }
    },
    selectDeliveryType(type) {
      this.deliveryType = type;
    },
    loadCartItems() {
      this.$store.dispatch("baskets/loadBasket", {
        path: this.$path,
      }).then((response) => {
        if (!response.items || !response.items.length) {
          location.href = `/${this.lang}/bag`;

          return;
        }

        this.showLoader = false;

        this.recalculate();
        if (this.redirect) {
          if (window.history && window.history.replaceState) {
            let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({path: newUrl}, "", newUrl);
          }
        }
      });
    },
    recalculate(country = null) {
      this.$store.dispatch("baskets/calculate", {
        path: this.$path,
        delivery_country: !!country ? country.id : null,
      }).then(prices => {
        console.log(prices);
      });
    },
    loadCountries() {
      this.LoadCountries({
        path: this.$path,
      });
    },
    handleBack() {
      history.back();
    },
    chooseAnother() {
      this.ChooseAnotherItems({
        path: this.$path,
        cart_id: this.cartItems[0].cart_id,
      });
    },
    order(type, skip = false) {
      if (this.allowedCountries.length > 0 && !skip) {
        if (!this.allowedCountries.includes(this.country_id.title)) {
          this.deliveryNotAvailable = true
          // this.$store.commit('changeModalTitle', '')
          // this.$store.commit('changeModalContent', "<p class=\"text-lg\"><span>Dear customer,</span><span>Unfortunately, direct shipments are currently available via checkout only for countries inside the European continent.</span><span>For shipments to countries outside Europe, please contact us at <a href=\"mailto:contact@forestal.com\">contact@forestal.com</a> and we will provide you all detailed information according to your delivery address.</span></p>")
          // this.$store.commit('changeModalType', 'text')
          // this.$store.commit('changeModalStatus', true)
          return
        }
      }

      if (!this.authorizing || !this.terms || !this.dataProtectionClause) {
        this.deliveryNotAvailable = false
        if (this.$refs.checkboxesErrorScrollRef) {
          this.$refs.checkboxesErrorScrollRef.scrollIntoView()

          if (!this.authorizing && this.$refs.authorizing) {
            this.$refs.authorizing.classList.add('error')
          }

          if (!this.terms && this.$refs.terms) {
            this.$refs.terms.classList.add('error')
          }

          if (!this.dataProtectionClause && this.$refs.dataProtectionClause) {
            this.$refs.dataProtectionClause.classList.add('error')
          }

          this.checkboxErrors = true

          setTimeout(() => {
            this.$refs.authorizing.classList.remove('error')
            this.$refs.terms.classList.remove('error')
            this.$refs.dataProtectionClause.classList.remove('error')

            this.checkboxErrors = false
          }, 3000)
        }
        // TODO: ADD ERRORS
        return
      }

      this.isLoading = true;
      this.paymentType = type;
      let data = {};
      if (!(!!localStorage.getItem("access_token"))) {
        data.f_name = this.f_name;
        data.l_name = this.l_name;
        data.email = this.email;
        data.password = this.password;
        data.password_confirmation = this.password_confirmation;
      }

      data.delivery_type = this.deliveryType;
      data.phone = this.phone;
      data.lang = this.lang;
      data.recipient = this.recipient;

      if (this.deliveryType === "new") {
        data.streetNo = this.streetNo;
        data.flat = this.flat;
        data.zip = this.zip;
        data.state = this.state;
        data.town_name = this.town_name;
        data.country_id = !!this.country_id ? this.country_id.id : null;
      }

      data.same_delivery = this.same_delivery;

      if (!data.same_delivery && this.deliveryType !== "pickup") {
        data.inv_streetNo = this.inv_streetNo;
        data.inv_flat = this.inv_flat;
        data.inv_zip = this.inv_zip;
        data.inv_state = this.inv_state;
        data.inv_town_name = this.inv_town_name;
        data.inv_country_id = !!this.inv_country_id ? this.inv_country_id.id : null;
      }

      if (this.deliveryType === "pickup") {
        data.same_delivery = true;
      }

      data.basket_id = this.$store.state.baskets.basket.id;
      data.payment_type = type;
      data.free = this.free;

      let ctx = this;

      let gtagData = {
        currency: "EUR",
        checkout_option: type,
        checkout_step: 1,
        items: [],
      };

      for (let item of this.items) {
        let newItem = {
          name: item.build["web_bike_title"],
          price: item.purchase_type !== "full"
            ? item.build["prices"]["res_price_for_eu"]
            : item.build["prices"]["price_for_eu"],
          quantity: item.qty,
          variant: item.build["web_bike_title"],
        };
        gtagData.items.push(newItem);
      }

      this.$store.dispatch("checkout/handle",
        {...{path: this.$path}, ...data})
        .then(result => {
          localStorage.removeItem("basket_id");
          this.$gtag.event("checkout_progress", gtagData);
          setTimeout(() => {
            if (window["fbq"]) {
              window["fbq"]("trackCustom", "ProceedCheckout", {
                currency: "EUR",
                value: 200,
                content_type: "product",
                num_items: gtagData.items.length,
                checkout_step: gtagData.checkout_step,
                checkout_option: gtagData.checkout_option,
                contents: gtagData.items.map(item => {
                  return {
                    id: item.variant,
                    quantity: item.quantity,
                  };
                }),
              });
            }
          });

          if (this.paymentType === "save") {
            this.deliveryNotAvailable = false
            this.orderSavedModalVisibility = true
          } else {
            location.href = result.data["pay_url"];
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 422) {
            if (Object.keys(err.response.data.errors).length > 0) {
              window.scrollTo(0, 0);
              for (let key of Object.keys(err.response.data.errors)) {
                if (ctx.$refs.hasOwnProperty(key)) {
                  let oldLabelText = ctx.$refs[key].innerText;
                  ctx.$refs[key].classList.add("error");
                  ctx.$refs[key].innerText = err.response.data.errors[key][0];
                  setTimeout(() => {
                    ctx.$refs[key].classList.remove("error");
                    ctx.$refs[key].innerText = oldLabelText;
                  }, 5000);
                }
              }
            } else {
              this.$store.commit(
                "setErrorText",
                this.trans.text.checkout["serverError"],
              );

              if (this.deliveryNotAvailable) {
                this.deliveryNotAvailable = false
                this.errorModal = true
                this.errorText = this.trans.text.checkout["serverError"]
              }
            }
          } else if (err.response && err.response.status === 400 && err.response.data.message) {
            this.$store.commit("toggleHeaderPopup", true);
            this.$store.commit(
              "setErrorText",
              err.response.data.message,
            );

            if (this.deliveryNotAvailable) {
              this.deliveryNotAvailable = false
              this.errorModal = true
              this.errorText = err.response.data.message
            }
          } else if (err.response && err.response.status === 401) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("user");
            this.$store.commit(
              "setErrorText",
              "Please login to your account",
            );

            if (this.deliveryNotAvailable) {
              this.deliveryNotAvailable = false
              this.errorModal = true
              this.errorText = "Please login to your account"
            }
          } else {
            this.$store.commit("toggleHeaderPopup", true);
            this.$store.commit(
              "setErrorText",
              this.trans.text.checkout["serverError"],
            );

            if (this.deliveryNotAvailable) {
              this.deliveryNotAvailable = false
              this.errorModal = true
              this.errorText = this.trans.text.checkout["serverError"]
            }
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.paymentType = null;
        });
    },
  },
  watch: {
    country_id: {
      handler: function (value, oldValue) {
        if (oldValue === null || value.id !== oldValue.id) {
          this.recalculate(value);
        }
        if (value.title) {
          if (this.allowedCountries.length > 0) {
            if (!this.allowedCountries.includes(value.title)) {
              this.deliveryNotAvailable = true
            }
          }
        }
      },
    },
    deliveryType: {
      handler: function (val) {
        if (val === "pickup") {
          this.same_delivery_option = {
            id: 2,
            title: "Other",
          };
          this.country_id = this.countries.find(item => {
            return item["title"] === "Andorra";
          });
          this.inv_recipient = null;
          this.inv_flat = null;
          this.inv_streetNo = null;
          this.inv_zip = null;
          this.inv_state = null;
          this.inv_country_id = null;
          this.inv_town_name = null;
        } else if (val !== "new") {
          this.country_id = this.countries.find(item => {
            return item["id"] === this.selectedDeliveryAddress.country_id;
          });
        } else {
          this.country_id = null;
        }
      },
    },
    same_delivery_option: {
      handler: function (val) {
        this.same_delivery = val.id === 1;
        if (this.same_delivery) {
          this.inv_recipient = null;
          this.inv_flat = null;
          this.inv_streetNo = null;
          this.inv_zip = null;
          this.inv_state = null;
          this.inv_country_id = null;
          this.inv_town_name = null;
        }
      }, deep: true, immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.checkout {
  position: relative;
}

.apple-ic,
.paypal-ic {
  margin-top: -2px;
}

.btn-modal-group {
  display: flex;
  justify-content: space-between;

  .btn {
    max-width: calc(50% - 5px);

    &:only-child {
      max-width: 100%;
    }
  }
}

.btn-gray {
  height: 54px;

  &.btn-sm {
    height: 44px;
  }

  .apple-ic,
  .paypal-ic {
    opacity: .5;
  }
}
</style>
<style lang="scss">
.multiselect__content-wrapper {
  overflow-x: hidden !important;
}

.with-margin {
  margin-bottom: 15px;
}

.no-hover {
  &:hover {
    color: #efefef;
    background: rgba(0, 0, 0, .5);
  }
}

.error-text {
  padding-top: 7px;
  color: #ff2207;
}

@media (max-width: 767px) {
  .checkout {
    padding-left: 24px;
    padding-right: 24px;
    box-sizing: border-box;
    max-width: 410px;
  }
}

.select-delivery-type {
  height: 40px;
  border: none;
  padding: 0 10px 0 10px;
  border-radius: 8px;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  color: #000000;
  transition: all 200ms ease;
  position: relative;
  cursor: pointer;

  &:after {
    content: '';
    position: absolute;
    top: 18px;
    right: 20px;
    border-color: #999 transparent transparent;
    border-style: solid;
    border-width: 5px 5px 0;
    transition: all 200ms ease;
  }

  .delivery-types-wrapper {
    width: 100%;
    max-height: 300px;
    display: none;
    box-sizing: border-box;
    left: 0;
    z-index: 50;
    overflow-y: auto;
    background-color: #f9f9f9;
    position: absolute;
    top: 100%;
    margin-top: -4px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;

    * {
      user-select: none;
    }

    .item {
      transition: all 200ms ease;
      padding: 10px 18px 10px 10px;

      &:last-of-type {
        padding: 14px 18px 20px 10px;
      }

      &:hover {
        background: #C2E900;
      }

      .title {
        font-size: 12px;
        font-weight: 700;
        color: #1C1C1C;
        margin-bottom: 15px;

        &.new-address {
          font-size: 14px;
          line-height: 1.4;
        }
      }

      &:first-of-type {
        .title {
          margin-bottom: 0;
        }
      }

      .info {
        font-size: 14px;
        font-weight: 400;
        color: #1C1C1C;
        padding-left: 10px;
        box-sizing: border-box;
        line-height: 21px;
      }
    }
  }

  &.open {
    &:after {
      transform: rotate(-180deg);
    }

    .delivery-types-wrapper {
      display: block;
    }
  }

  .selected-delivery-type {
    width: 100%;
    height: 100%;
    line-height: 40px;
    font-size: 14px;
    color: #1C1C1C;
    overflow: hidden;
    word-wrap: unset;

    &.pickup {
      font-size: 12px;
    }
  }
}

.custom-primary-modal {
  display: none;

  &.active {
    display: flex;
  }
}

.checkout-form .error-text {
  padding-top: 0;
}
</style>
